/**
 * The agency's role catalogue, and the roles its configuration gives each
 * archetype, for the clinic roles and Custom's pre-ticked agency roles.
 *
 * Fetched when the dialog opens. A failed load is reported rather than read as
 * "no roles", and the next open tries again.
 */

import { useEffect, useState } from 'react';
import { listRoles } from '../../../services/api';
import type { RoleResponse } from '../../../services/api';
import { getAgencyConfig } from '../../../services/agencyConfig';

export interface OnboardRoleContext {
  roles: RoleResponse[];
  effectiveArchetypeRoles: Record<string, string[]> | null;
  isLoading: boolean;
  /** The catalogue could not be loaded, so clinic roles cannot be sent. */
  failed: boolean;
}

export function useOnboardRoleContext(open: boolean): OnboardRoleContext {
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [effectiveArchetypeRoles, setEffective] = useState<Record<
    string,
    string[]
  > | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [failed, setFailed] = useState(false);

  // Closing clears a failure, so reopening the dialog retries.
  useEffect(() => {
    if (!open) setFailed(false);
  }, [open]);

  useEffect(() => {
    if (!open || loadedOnce || isLoading || failed) return;
    setIsLoading(true);
    Promise.all([
      listRoles({ query: { limit: 500 }, throwOnError: true }),
      // Tolerated separately: without it Custom just pre-ticks nothing.
      getAgencyConfig().catch(() => null),
    ])
      .then(([rolesRes, config]) => {
        setRoles(rolesRes.data ?? []);
        setEffective(
          (config?.effective_archetype_roles as Record<string, string[]>) ??
            null
        );
        setLoadedOnce(true);
      })
      .catch(err => {
        console.error('Failed to load roles for the add-member dialog:', err);
        setFailed(true);
      })
      .finally(() => setIsLoading(false));
  }, [open, loadedOnce, isLoading, failed]);

  return { roles, effectiveArchetypeRoles, isLoading, failed };
}
